'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const relatedServices = [
  { label: 'Container Transport', href: '/container-transport' },
  { label: 'Airfreight Transport', href: '/airfreight-transport' },
  { label: 'Inland Transport', href: '/inland-transport' },
  { label: 'Customs Representation', href: '/customs-representation' },
  { label: 'Port Operations', href: '/port-operations' },
  { label: 'Cargo Insurance', href: '/cargo-insurance' },
  { label: 'Bank Guarantee', href: '/bank-guarantee' },
];

export default function LCLTransportPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero section */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm lg:max-w-md" aria-label="LCL groupage cargo illustration">
                <rect x="30" y="80" width="260" height="140" rx="8" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <line x1="117" y1="80" x2="117" y2="220" stroke="#1B6B8A" strokeWidth="3" strokeDasharray="8 4"/>
                <line x1="203" y1="80" x2="203" y2="220" stroke="#1B6B8A" strokeWidth="3" strokeDasharray="8 4"/>
                <rect x="48" y="100" width="52" height="48" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.08)"/>
                <line x1="48" y1="124" x2="100" y2="124" stroke="#1B6B8A" strokeWidth="2"/>
                <line x1="74" y1="100" x2="74" y2="148" stroke="#1B6B8A" strokeWidth="2"/>
                <rect x="134" y="96" width="44" height="56" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.08)"/>
                <line x1="134" y1="124" x2="178" y2="124" stroke="#1B6B8A" strokeWidth="2"/>
                <rect x="220" y="104" width="48" height="40" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.08)"/>
                <rect x="48" y="162" width="36" height="36" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.08)"/>
                <rect x="134" y="164" width="44" height="34" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.08)"/>
                <rect x="220" y="158" width="48" height="42" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.08)"/>
                <path d="M160 30 L160 72" stroke="#2A8FAB" strokeWidth="3" strokeLinecap="round"/>
                <circle cx="160" cy="22" r="10" stroke="#1B6B8A" strokeWidth="3" fill="none"/>
                <path d="M156 22 L160 18 L164 22 M160 18 L160 28" stroke="#1B6B8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <text x="160" y="255" textAnchor="middle" fill="#3A5070" fontSize="13" fontFamily="sans-serif">Consolidated Shipment</text>
              </svg>
            </div>

            {/* Text */}
            <div>
              <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
                Less Container Load (LCL)
              </h1>
              <div className="space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: '#3A5070' }}>
                <p>
                  Less Container Load (LCL) shipping, also known as groupage, is the perfect solution for businesses that do not have enough cargo to fill an entire container. <strong style={{ color: '#0F2340' }}>World Transport Overseas</strong> consolidates your shipment with other compatible cargo, allowing you to share the container space and costs.
                </p>
                <p>
                  Our LCL service provides a cost-effective and flexible option for shipping smaller volumes of goods internationally. You only pay for the cubic meters or weight your cargo occupies, making it an economical choice for regular smaller shipments or when testing new markets.
                </p>
                <p>
                  With our extensive network of consolidation hubs and partner agents worldwide, we offer frequent LCL sailings to all major ports, ensuring your cargo reaches its destination reliably and on schedule — regardless of volume. Our experienced team handles all documentation and customs formalities, providing a seamless door-to-door service.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  href="/transport-quote"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-md"
                  style={{ background: '#1A5FAB' }}
                >
                  Get a Quote
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key highlights */}
        <section className="py-14" style={{ background: '#0F2340' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Why choose WTO LCL?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Cost Savings', desc: 'Pay only for the space your cargo occupies — no wasted spend on empty container space' },
                { title: 'Flexible Volumes', desc: 'Ship as little as 1 CBM — ideal for small businesses and sample shipments' },
                { title: 'Global Coverage', desc: 'Regular LCL consolidations to all major ports worldwide via our hub network' },
                { title: 'Weekly Sailings', desc: 'Frequent consolidation schedules ensuring your cargo moves without long waiting times' },
              ]?.map((h, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="w-8 h-8 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'rgba(126,200,220,0.2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.21.45 4.55 1.23" />
                    </svg>
                  </div>
                  <p className="font-bold text-base mb-1" style={{ color: '#FFFFFF' }}>{h?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{h?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14" style={{ background: '#0A1628' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Other Services</h2>
            <div className="flex flex-wrap gap-3">
              {relatedServices?.map((s) => (
                <Link
                  key={s?.href}
                  href={s?.href}
                  className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80"
                  style={{ background: '#132848', color: '#B8CDE0', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
